/**
 * Image helpers for the avatar editor (no dependencies):
 *  - loadImage(): decode a picked file with EXIF orientation applied
 *  - makePreview(): small JPEG object URL for the on-screen cropper (orientation already baked in)
 *  - cropToSquareJpeg(): square crop → JPEG blob at an exact size, with progressive down-scaling
 *  - pure crop maths (clampCrop, panCrop, zoomCropAt …) shared by the UI and unit tests
 *
 * iOS has a hard limit on total canvas memory, so every canvas is shrunk to 0×0 as soon as
 * it is no longer needed.
 */

export interface LoadedImage {
  source: CanvasImageSource;
  /** Oriented pixel size (EXIF rotation applied). */
  width: number;
  height: number;
  /** Free decoded pixels. Safe to call more than once. */
  release(): void;
}

/** Square crop in source pixels. */
export interface SquareCrop {
  x: number;
  y: number;
  size: number;
}

/** Maximum zoom: the crop side may shrink to 1/MAX_ZOOM of the image's short side. */
export const MAX_ZOOM = 4;
export const AVATAR_JPEG_QUALITY = 0.85;

/* ─── Pure crop maths ───────────────────────────────────────────────────── */

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Largest centred square. */
export function initialCrop(w: number, h: number): SquareCrop {
  const size = Math.min(w, h);
  return { x: (w - size) / 2, y: (h - size) / 2, size };
}

/** Keep the crop inside the image and within the zoom limits. */
export function clampCrop(c: SquareCrop, w: number, h: number, maxZoom = MAX_ZOOM): SquareCrop {
  const full = Math.min(w, h);
  const size = clamp(c.size, full / maxZoom, full);
  return { size, x: clamp(c.x, 0, w - size), y: clamp(c.y, 0, h - size) };
}

/** Zoom factor of a crop (1 = whole short side visible). */
export function zoomOf(c: SquareCrop, w: number, h: number): number {
  return Math.min(w, h) / c.size;
}

/** Move the image by (dx, dy) on-screen pixels in a square viewport of `view` pixels. */
export function panCrop(c: SquareCrop, dx: number, dy: number, view: number, w: number, h: number): SquareCrop {
  if (view <= 0) return c;
  const k = c.size / view; // source px per screen px
  return clampCrop({ size: c.size, x: c.x - dx * k, y: c.y - dy * k }, w, h);
}

/**
 * Zoom to `zoom` keeping the image point under the viewport position (fx, fy) fixed
 * (pinch midpoint, wheel cursor, or the centre for the slider).
 */
export function zoomCropAt(
  c: SquareCrop,
  zoom: number,
  fx: number,
  fy: number,
  view: number,
  w: number,
  h: number,
  maxZoom = MAX_ZOOM,
): SquareCrop {
  if (view <= 0) return c;
  const full = Math.min(w, h);
  const size = full / clamp(zoom, 1, maxZoom);
  const px = c.x + (fx * c.size) / view;
  const py = c.y + (fy * c.size) / view;
  return clampCrop({ size, x: px - (fx * size) / view, y: py - (fy * size) / view }, w, h, maxZoom);
}

/**
 * Sizes for progressive down-scaling (each step at most halves), ending exactly at `target`.
 * E.g. 3024 → 512 gives [2048, 1024, 512].
 */
export function downscaleSteps(from: number, target: number): number[] {
  const steps: number[] = [];
  if (from <= target * 2) return [target];
  let n = Math.floor(Math.log2(from / target));
  // every intermediate size must be strictly smaller than the source
  while (n > 0 && target * 2 ** n >= from) n--;
  for (let i = n; i >= 1; i--) steps.push(target * 2 ** i);
  steps.push(target);
  return steps;
}

/* ─── DOM helpers ───────────────────────────────────────────────────────── */

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  return c;
}

function ctx2d(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = c.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('canvas_unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  return ctx;
}

function freeCanvas(c: HTMLCanvasElement): void {
  c.width = 0;
  c.height = 0;
}

function toBlob(c: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    c.toBlob((b) => (b ? resolve(b) : reject(new Error('encode_failed'))), type, quality);
  });
}

/** Decode an image file. Uses createImageBitmap (EXIF-aware) with an <img> fallback. */
export async function loadImage(file: Blob): Promise<LoadedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      if (bmp.width > 0 && bmp.height > 0) {
        let closed = false;
        return {
          source: bmp,
          width: bmp.width,
          height: bmp.height,
          release() {
            if (!closed) bmp.close();
            closed = true;
          },
        };
      }
      bmp.close();
    } catch {
      /* older Safari rejects the options bag or the format → <img> fallback */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    if (!img.naturalWidth || !img.naturalHeight) throw new Error('decode_failed');
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      release() {
        img.removeAttribute('src');
      },
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** A small JPEG (object URL) of the whole image for the cropper. Caller revokes `url`. */
export async function makePreview(img: LoadedImage, maxSide = 1600): Promise<{ url: string; width: number; height: number }> {
  const k = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * k));
  const h = Math.max(1, Math.round(img.height * k));
  const c = makeCanvas(w, h);
  try {
    const ctx = ctx2d(c);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img.source, 0, 0, img.width, img.height, 0, 0, w, h);
    const blob = await toBlob(c, 'image/jpeg', 0.9);
    return { url: URL.createObjectURL(blob), width: w, height: h };
  } finally {
    freeCanvas(c);
  }
}

/** Crop a square from `img` and encode it as a `size`×`size` JPEG. */
export async function cropToSquareJpeg(img: LoadedImage, crop: SquareCrop, size: number, quality = AVATAR_JPEG_QUALITY): Promise<Blob> {
  const c0 = clampCrop(crop, img.width, img.height, Number.POSITIVE_INFINITY);
  const steps = downscaleSteps(c0.size, size);
  let prev: HTMLCanvasElement | null = null;
  try {
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i]!;
      const next = makeCanvas(s, s);
      const ctx = ctx2d(next);
      ctx.fillStyle = '#fff'; // transparent PNG areas become white, not black
      ctx.fillRect(0, 0, s, s);
      if (prev) ctx.drawImage(prev, 0, 0, prev.width, prev.height, 0, 0, s, s);
      else ctx.drawImage(img.source, c0.x, c0.y, c0.size, c0.size, 0, 0, s, s);
      if (prev) freeCanvas(prev);
      prev = next;
    }
    return await toBlob(prev!, 'image/jpeg', quality);
  } finally {
    if (prev) freeCanvas(prev);
  }
}
